import * as React from "react";
import { RelatedReturn } from "@/components/related-link";
import { cn } from "@/lib/utils";

/** Compact page chrome: a wrapping header row with title, meta and actions, then the body. */
export function Page({ title, meta, actions, children, className }: { title: React.ReactNode; meta?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex h-full min-w-0 flex-col", className)}>
      <div className="flex min-h-11 shrink-0 flex-wrap items-center gap-x-3 gap-y-2 bg-panel px-4 py-2 hairline-b">
        <RelatedReturn />
        <h1 className="text-sm font-semibold">{title}</h1>
        {meta && <span className="truncate text-xs text-muted-foreground">{meta}</span>}
        <div className="flex-1" />
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      <div className="relative min-h-0 min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function Toolbar({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-10 shrink-0 flex-wrap items-center gap-2 px-3 py-1.5 hairline-b">{children}</div>;
}

/**
 * The note a document the console does not own carries: the portal owns
 * the users of the companies it manages, and one device per user per
 * platform (portal-integration.md section 3.5).
 */
export function ManagedNote({ children }: { children: React.ReactNode }) {
  return <div className="shrink-0 rounded-sm border border-border bg-muted/40 px-2 py-1.5 text-2xs text-muted-foreground">{children}</div>;
}

export function StatusDot({ state, className }: { state: "ok" | "warn" | "bad" | "off"; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block size-1.5 rounded-full align-middle",
        state === "ok" && "bg-ok",
        state === "warn" && "bg-warn",
        state === "bad" && "bg-bad",
        state === "off" && "bg-border",
        className,
      )}
      aria-hidden="true"
    />
  );
}

export function Empty({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 p-8 text-center">
      <div className="text-sm font-medium">{title}</div>
      {children && <div className="max-w-md text-xs text-muted-foreground">{children}</div>}
    </div>
  );
}

/** Label/value grid used by inspectors. */
export function KV({ rows }: { rows: [label: React.ReactNode, value: React.ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
      {rows.map(([k, v], i) => (
        <React.Fragment key={i}>
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="min-w-0 truncate">{v ?? <span className="text-muted-foreground">—</span>}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}
